import { Link } from "react-router-dom";
import TopicCard from "@/components/topic/TopicCard";
import { categories, hotRank, topics } from "@/mocks/topics";
import { formatNumber, heatBadgeClass, toneClass, toneText } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";

const maxHeat = hotRank[0].heat;

export default function Home() {
  usePageMeta(
    "天涯吃瓜 - 话题发酵追踪 热议焦点与舆论风向观察",
    "天涯吃瓜话题发酵追踪器，聚焦热议焦点与舆论风向变化。实时监测话题升温动态，汇总网友评论观点，帮你捕捉最新讨论热度走势，看懂舆论如何发酵与转向。",
    "天涯吃瓜,天涯吃瓜话题发酵,天涯吃瓜热议焦点,天涯吃瓜舆论风向,天涯吃瓜网友评论"
  );

  const trendTopic = hotRank[0];
  const voiceTopics = [topics[0], topics[1]];

  return (
    <>
      {/* 首屏品牌区 */}
      <section className="relative overflow-hidden border-b border-background-200 bg-gradient-to-br from-secondary-50 via-background-50 to-accent-100/50">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-[1.1fr_1fr]">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500 live-dot" aria-hidden="true"></span>
              舆论风向 · 实时监测中
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black leading-tight tracking-tight text-foreground-950 md:text-[44px]">
              每一次爆红，
              <br />
              都<em className="not-italic text-primary-600">有迹可循</em>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-foreground-600 md:text-lg">
              天涯吃瓜聚焦热议焦点与舆论风向变化，实时追踪话题升温动态，汇总网友评论观点。让你在喧嚣的讨论中，快速看懂热度如何发酵、观点如何转向。
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/topics"
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-fire-line text-base" aria-hidden="true"></i>
                围观今日热议
              </Link>
              <Link
                to="/about"
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-white px-6 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                认识天涯吃瓜
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-foreground-500">
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600">1,286</p>
                <p className="mt-0.5 text-xs">今日热议话题</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-accent-600">46</p>
                <p className="mt-0.5 text-xs">正在发酵中</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-secondary-700">38.7万</p>
                <p className="mt-0.5 text-xs">累计观察动态</p>
              </div>
            </div>
          </div>

          {/* 实时热议榜 */}
          <div className="fade-up fade-up-2">
            <div className="rounded-2xl border border-background-200 bg-white/90 p-5 md:p-6">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
                  <i className="ri-flashlight-line text-primary-500" aria-hidden="true"></i>
                  实时热议榜
                </h2>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-500 live-dot" aria-hidden="true"></span>
                  LIVE
                </span>
              </div>
              <ol className="mt-4 space-y-3">
                {hotRank.slice(0, 5).map((t, i) => (
                  <li key={t.id}>
                    <Link
                      to={`/topic/${t.id}`}
                      className="group flex items-center gap-3 rounded-lg p-1.5 transition-colors hover:bg-background-50"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          i === 0
                            ? "bg-primary-500 text-white"
                            : i === 1
                            ? "bg-accent-500 text-foreground-950"
                            : "bg-secondary-100 text-secondary-700"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground-800 group-hover:text-primary-600">
                          {t.title}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="h-1 flex-1 overflow-hidden rounded-full bg-background-100">
                            <span
                              className="block h-full rounded-full bg-gradient-to-r from-accent-400 to-primary-500"
                              style={{ width: `${(t.heat / maxHeat) * 100}%` }}
                            ></span>
                          </span>
                          <span className="shrink-0 text-xs text-foreground-400">{formatNumber(t.heat)}</span>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* 观察栏目 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-xs font-semibold tracking-wide text-primary-600">观察栏目</span>
            <h2 className="mt-1 font-heading text-xl font-bold text-foreground-900 md:text-2xl">你想围观哪一类热点？</h2>
          </div>
          <Link to="/topics" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
            全部热议焦点
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {categories
            .filter((c) => c.slug !== "all")
            .map((c) => {
              const count = topics.filter((t) => t.categorySlug === c.slug).length;
              return (
                <Link
                  key={c.slug}
                  to={`/topics?cat=${c.slug}`}
                  className="group flex flex-col items-start gap-2 rounded-xl border border-background-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 transition group-hover:bg-primary-100 group-hover:text-primary-600">
                    <i className={`${c.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <span className="text-sm font-semibold text-foreground-800">{c.name}</span>
                  <span className="text-xs text-foreground-400">{count} 个热议焦点</span>
                </Link>
              );
            })}
        </div>
      </section>

      {/* 热议焦点 */}
      <section className="border-y border-background-200 bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <span className="text-xs font-semibold tracking-wide text-primary-600">HOT &amp; TRENDING</span>
              <h2 className="mt-1 font-heading text-xl font-bold text-foreground-900 md:text-2xl">正在发酵的热议焦点</h2>
              <p className="mt-2 max-w-2xl text-sm text-foreground-500">
                热度不等于喧嚣，我们带你拆解每一条热点是如何被顶上来、又为何转向。
              </p>
            </div>
            <Link
              to="/topics"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              查看全部
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {topics.slice(0, 6).map((t) => (
              <TopicCard key={t.id} topic={t} />
            ))}
          </div>
        </div>
      </section>

      {/* 热度走势解读 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="overflow-hidden rounded-2xl border border-background-200 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative min-h-[260px] lg:min-h-[320px]">
              <img
                src={trendTopic.cover}
                alt={trendTopic.title}
                title={`${trendTopic.title} - 天涯吃瓜热度走势解读`}
                className="h-full w-full object-cover object-top"
              />
              <span
                className={`absolute left-4 top-4 inline-flex h-7 items-center gap-1 rounded-full px-3 text-xs font-semibold ${heatBadgeClass[trendTopic.heatLabel]}`}
              >
                <i className="ri-fire-line text-sm" aria-hidden="true"></i>
                今日头条热议
              </span>
            </div>
            <div className="flex flex-col justify-center p-6 md:p-10">
              <span className="text-xs font-semibold tracking-wide text-accent-600">本期热度解剖</span>
              <h2 className="mt-2 font-heading text-lg font-bold text-foreground-900 md:text-xl">
                {trendTopic.title}
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-500">{trendTopic.excerpt}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {trendTopic.tags.map((tag) => (
                  <span key={tag} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                    #{tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/topic/${trendTopic.id}`}
                className="mt-6 inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                查看发酵脉络
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 舆论声浪 */}
      <section className="border-y border-background-200 bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <span className="text-xs font-semibold tracking-wide text-primary-600">声浪观察</span>
            <h2 className="mt-1 font-heading text-xl font-bold text-foreground-900 md:text-2xl">网友观点都在往哪边倒？</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-foreground-500">
              我们不替谁发声，只把评论区的声音分门别类、归纳成看得懂的观点结构。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {voiceTopics.map((t) => (
              <div key={t.id} className="rounded-2xl border border-background-200 bg-white p-6">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-400">{t.categoryName}</span>
                  <span className="text-xs text-foreground-400">{t.date}</span>
                  <span
                    className={`ml-auto inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-semibold ${heatBadgeClass[t.heatLabel]}`}
                  >
                    <i className="ri-fire-line" aria-hidden="true"></i>
                    {t.heatLabel}
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-900 md:text-lg">
                  <Link to={`/topic/${t.id}`} className="hover:text-primary-600">
                    {t.title}
                  </Link>
                </h3>
                <div className="mt-5 space-y-3">
                  {t.opinions.map((o) => (
                    <div key={o.stance} className="rounded-lg border border-background-100 bg-background-50/60 p-3.5">
                      <div className="flex items-center justify-between">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${toneClass[o.tone]}`}
                        >
                          <i
                            className={`${o.tone === "agree" ? "ri-thumb-up-line" : o.tone === "against" ? "ri-thumb-down-line" : "ri-discuss-line"} text-xs`}
                            aria-hidden="true"
                          ></i>
                          {toneText[o.tone]} · {o.stance}
                        </span>
                        <span className="font-heading text-sm font-bold text-foreground-700">{o.pct}%</span>
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-foreground-500">
                        {o.points[0]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 口碑 */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <span className="text-xs font-semibold tracking-wide text-accent-600">他们怎么看天涯吃瓜</span>
            <h2 className="mt-1 font-heading text-xl font-bold text-foreground-900 md:text-2xl">
              围观群众的口碑，
              <br />
              是最好的热度说明
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              自上线以来，天涯吃瓜帮助大量围观群众快速理清热议焦点的来龙去脉，用一张时间线、一组观点结构，把喧嚣的网络吵声整理成清晰的答案。
            </p>
            <Link
              to="/about"
              className="mt-5 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              了解更多
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              {
                name: "追热点的小林",
                role: "新媒体编辑",
                text: "以前追热点全靠感觉，现在看一眼热度走势和时间线就知道它到哪个阶段了，选题省了很多力气。",
              },
              {
                name: "不熬夜的老王",
                role: "互联网从业者",
                text: "最喜欢它把评论区观点归纳成占比结构，五分钟就能知道大家大概站哪边，比刷半天评论高效。",
              },
              {
                name: "北方的雪",
                role: "在读研究生",
                text: "写舆情作业时会来这找脉络参考，观点归类和发酵节点很清晰，能帮助理解一次讨论是怎么起来的。",
              },
              {
                name: "爱吃瓜的阿黎",
                role: "普通网友",
                text: "页面干净、看着舒服，关键是把乱哄哄的热点讲明白了，围观也能围个明白。",
              },
            ].map((c) => (
              <figure key={c.name} className="rounded-xl border border-background-200 bg-white p-5">
                <div className="flex gap-1 text-accent-500" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-6 text-foreground-600">“{c.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-600">
                    {c.name.charAt(0)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-800">{c.name}</span>
                    <span className="block text-xs text-foreground-400">{c.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ 引导 + CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-8 overflow-hidden rounded-2xl border border-background-200 bg-gradient-to-r from-primary-50 via-background-50 to-accent-100/60 p-6 md:p-10 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <span className="text-xs font-semibold tracking-wide text-primary-600">常见问题</span>
            <h2 className="mt-1 font-heading text-xl font-bold text-foreground-900 md:text-2xl">
              对天涯吃瓜还有疑问？
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-7 text-foreground-500">
              关于热度如何统计、观点如何归纳、为什么有些话题不在榜上……这些问题我们在常见问题里都做了说明。
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/faq"
              className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              查看常见问题
              <i className="ri-question-line" aria-hidden="true"></i>
            </Link>
            <Link
              to="/topics"
              className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-white px-6 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              去围观热榜
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}